import { CheckCheck, FileText, Play, ExternalLink, Phone, Reply, List, BellOff, ShoppingBag } from "lucide-react";

const BTN_ICON = { url: ExternalLink, phone: Phone, quick_reply: Reply, unsubscribe: BellOff, catalog: ShoppingBag };
const FIXED_TEXT = { unsubscribe: "Disiscriviti", catalog: "Vedi catalogo" };

function Buttons({ btns }) {
  const live = (btns || []).filter((b) => (b.text || "").trim() || FIXED_TEXT[b.type]);
  if (live.length === 0) return null;
  // WhatsApp mostra al massimo 2 pulsanti; dal terzo in poi compare «Vedi tutte le opzioni»
  const shown = live.length > 2 ? live.slice(0, 2) : live;
  return (
    <div className="mt-1 space-y-1" data-testid="preview-buttons">
      {shown.map((b, i) => {
        const Ic = BTN_ICON[b.type] || Reply;
        return (
          <div key={i} className="bg-[#1F2C34] rounded-md py-1.5 text-center text-[12px] text-[#53BDEB] flex items-center justify-center gap-1">
            <Ic className="h-3.5 w-3.5" />{FIXED_TEXT[b.type] || b.text}
          </div>
        );
      })}
      {live.length > 2 && (
        <div className="bg-[#1F2C34] rounded-md py-1.5 text-center text-[12px] text-[#53BDEB] flex items-center justify-center gap-1" data-testid="preview-see-all">
          <List className="h-3.5 w-3.5" />Vedi tutte le opzioni
        </div>
      )}
    </div>
  );
}

export const EXAMPLES = {
  nome: "Marco", name: "Marco", email: "marco.rossi@email.it", store: "Milano Centro", punto_vendita: "Milano Centro",
  city: "Milano", "città": "Milano", citta: "Milano", card_code: "VC-00421", codice_carta: "VC-00421",
  sconto: "20%", scadenza: "31/12/2026", codice: "PROMO20", importo: "€ 50,00", ordine: "#1042",
  taglia: "L", compleanno: "12/05", data: "15/06/2026", ora: "18:30", negozio: "Milano Centro", prodotto: "Giacca in pelle",
  "1": "Marco", "2": "20%", "3": "31/12/2026", "4": "PROMO20",
};
export const exampleValue = (raw) => {
  const inner = String(raw || "").trim();
  const [k, fb] = inner.includes("|") ? inner.split("|", 2).map((s) => s.trim()) : [inner, null];
  if (EXAMPLES[k] !== undefined) return EXAMPLES[k];
  if (fb) return fb;
  return k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, " ");
};
export const fillExamples = (t, overrides = {}) =>
  (t || "").replace(/\{\{([^{}]+)\}\}/g, (_, raw) => {
    const k = raw.split("|")[0].trim();
    return overrides[k] !== undefined ? overrides[k] : exampleValue(raw);
  });

export default function WhatsAppPreview({ body, imageUrl, header, footer, buttons = [], carousel = [], name = "Marco" }) {
  const fill = (t) => fillExamples(t, { nome: name, name });
  const time = new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  const hasCarousel = !!(carousel && carousel.length > 0);
  // Con il carosello Meta non invia intestazione, piè di pagina e pulsanti principali: l'anteprima li nasconde
  const hdr = hasCarousel ? null : (header && header.type && header.type !== "none" ? header : (imageUrl ? { type: "image", media_url: imageUrl } : null));

  const renderHeader = (h) => {
    if (!h) return null;
    if (h.type === "text") return h.text ? <p className="text-[13px] font-bold text-white px-1 pt-1">{fill(h.text)}</p> : null;
    if (h.type === "image") return h.media_url ? <img src={h.media_url} alt="" className="rounded-md mb-1 w-full object-cover max-h-40" onError={(e) => { e.target.style.display = "none"; }} /> : null;
    if (h.type === "video") return <div className="rounded-md mb-1 w-full h-28 bg-black/50 flex items-center justify-center"><Play className="h-8 w-8 text-white/80" /></div>;
    if (h.type === "document") return <div className="rounded-md mb-1 w-full bg-black/30 flex items-center gap-2 p-2"><FileText className="h-6 w-6 text-white/80" /><span className="text-xs text-white/80 truncate">Documento PDF</span></div>;
    return null;
  };

  return (
    <div data-testid="whatsapp-live-preview" className="rounded-[2rem] border-[6px] border-[#1F2937] bg-[#0B141A] overflow-hidden w-full max-w-[300px] mx-auto shadow-2xl">
      <div className="bg-[#1F2C34] px-4 py-3 flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-[#10B981] flex items-center justify-center text-[#0A0E17] font-bold text-sm">A</div>
        <div className="flex-1">
          <p className="text-slate-100 text-sm font-medium">Azienda</p>
          <p className="text-[#10B981] text-[10px]">business account</p>
        </div>
      </div>
      <div
        className="px-3 py-5 min-h-[260px] flex flex-col justify-end gap-2"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect width='40' height='40' fill='%230b141a'/%3E%3Ccircle cx='20' cy='20' r='1' fill='%23ffffff' fill-opacity='0.03'/%3E%3C/svg%3E\")",
        }}
      >
        <div className="self-end max-w-[85%] w-full bg-[#005C4B] rounded-lg rounded-tr-none p-1.5 shadow">
          {renderHeader(hdr)}
          <p className="text-[13px] text-slate-50 whitespace-pre-wrap leading-snug px-1 pt-1">
            {fill(body) || "La tua anteprima del messaggio apparirà qui..."}
          </p>
          {footer && !hasCarousel && <p className="text-[11px] text-slate-300/60 px-1 pt-1">{fill(footer)}</p>}
          <div className="flex items-center justify-end gap-1 px-1">
            <span className="text-[10px] text-slate-300/70">{time}</span>
            <CheckCheck className="h-3.5 w-3.5 text-[#53BDEB]" />
          </div>
          {!hasCarousel && <Buttons btns={buttons} />}
        </div>

        {carousel && carousel.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="preview-carousel">
            {carousel.map((c, i) => (
              <div key={i} className="min-w-[160px] max-w-[160px] bg-[#005C4B] rounded-lg p-1.5 shadow shrink-0">
                {c.image_url
                  ? <img src={c.image_url} alt="" className="rounded-md w-full h-20 object-cover mb-1" onError={(e) => { e.target.style.display = "none"; }} />
                  : <div className="rounded-md w-full h-20 bg-black/30 mb-1" />}
                <p className="text-[12px] text-slate-50 whitespace-pre-wrap leading-snug px-0.5">{fill(c.body)}</p>
                <Buttons btns={c.buttons} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
